/**
 * Utilidades para formatear y validar DUI (Documento Único de Identidad) de El Salvador
 */

/**
 * Formatea un DUI al formato correcto ########-#
 * @param dui - El DUI a formatear (puede venir con o sin guión)
 * @returns DUI formateado o null si es inválido
 */
export const formatDUI = (dui: string): string | null => {
  if (!dui) return null;
  
  // Remover cualquier caracter que no sea dígito
  const cleanDui = dui.replace(/\D/g, '');
  
  // Verificar que tenga exactamente 9 dígitos
  if (cleanDui.length !== 9) {
    console.warn(`DUI inválido: debe tener 9 dígitos, recibido: ${cleanDui} (${cleanDui.length} dígitos)`);
    return null;
  }
  
  // Formatear como ########-#
  return `${cleanDui.substring(0, 8)}-${cleanDui.substring(8)}`;
};

/**
 * Valida si un DUI está en el formato correcto
 * @param dui - El DUI a validar
 * @returns true si es válido, false si no
 */
export const validateDUI = (dui: string): boolean => {
  if (!dui) return false;
  
  // Patrón para DUI: 8 dígitos, guión, 1 dígito
  const duiPattern = /^\d{8}-\d$/;
  return duiPattern.test(dui);
};

/**
 * Genera un DUI válido a partir de un código de paciente
 * @param codigoPaciente - El código del paciente
 * @returns DUI formateado o DUI genérico
 */
export const generateDUIFromCode = (codigoPaciente: string): string => {
  if (!codigoPaciente) {
    return "00000000-0"; // DUI genérico para consumidor final
  }
  
  // Si ya tiene formato de DUI, devolverlo
  if (validateDUI(codigoPaciente)) {
    return codigoPaciente;
  }
  
  // Si es solo números y tiene longitud suficiente, formatear
  const cleanCode = codigoPaciente.replace(/\D/g, '');
  if (cleanCode.length >= 8) {
    const formatted = formatDUI(cleanCode.substring(0, 9));
    if (formatted) return formatted;
  }
  
  // Generar DUI basado en el código del paciente
  const paddedCode = cleanCode.padEnd(8, '0').substring(0, 8);
  const checkDigit = calculateDUICheckDigit(paddedCode);
  
  return `${paddedCode}-${checkDigit}`;
};

/**
 * Calcula el dígito verificador del DUI según el algoritmo oficial
 * @param duiWithoutCheck - Los primeros 8 dígitos del DUI
 * @returns El dígito verificador (0-9)
 */
export const calculateDUICheckDigit = (duiWithoutCheck: string): number => {
  if (duiWithoutCheck.length !== 8 || !/^\d{8}$/.test(duiWithoutCheck)) {
    return 0; // Retornar 0 si no es válido
  }
  
  const multipliers = [3, 7, 6, 1, 8, 9, 4, 5];
  let sum = 0;
  
  for (let i = 0; i < 8; i++) {
    sum += parseInt(duiWithoutCheck[i]) * multipliers[i];
  }
  
  const remainder = sum % 11;
  
  if (remainder < 2) {
    return remainder;
  } else {
    return 11 - remainder;
  }
};

/**
 * Formatea un número de documento según el tipo
 * @param documento - El documento a formatear
 * @param tipoDocumento - El tipo de documento ("13" para DUI, "36" para NIT)
 * @returns Documento formateado
 */
export const formatDocumento = (documento: string, tipoDocumento: string): string => {
  if (tipoDocumento === "13" || tipoDocumento === "36") {
    // Es DUI, aplicar formato específico
    const formatted = formatDUI(documento);
    return formatted || generateDUIFromCode(documento);
  }
  
  // Para otros tipos de documento, retornar tal como está
  return documento;
};
